import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Calendar, DollarSign, Users, Trophy, Check, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const SchedulesPricing = ({ user, onLogout }) => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} onLogout={onLogout} />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-linear-to-br from-cyan-50 via-white to-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeIn} className="text-center">
            <h1 className="text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">
              Schedules & Pricing
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Flexible schedules and transparent pricing for all our swimming programs. 
              Find the perfect fit for you and your family.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Facility Hours */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeIn} className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Facility Hours</h2>
            <p className="text-lg text-slate-600">Achimota Swimming & Leisure Center</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div {...fadeIn} transition={{ delay: 0.1 }}>
              <Card className="h-full border-2 border-cyan-200">
                <CardHeader className="bg-cyan-50">
                  <CardTitle className="flex items-center text-cyan-700">
                    <Calendar className="w-6 h-6 mr-2" />
                    Weekdays (Monday - Friday)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="font-medium">Opening</span>
                      <span className="text-cyan-700 font-bold">7:00 AM</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="font-medium">Closing</span>
                      <span className="text-cyan-700 font-bold">7:00 PM</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div {...fadeIn} transition={{ delay: 0.2 }}>
              <Card className="h-full border-2 border-orange-200">
                <CardHeader className="bg-orange-50">
                  <CardTitle className="flex items-center text-orange-700">
                    <Calendar className="w-6 h-6 mr-2" />
                    Weekends (Saturday - Sunday)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="font-medium">Opening</span>
                      <span className="text-orange-700 font-bold">8:30 AM</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="font-medium">Closing</span>
                      <span className="text-orange-700 font-bold">7:00 PM</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Learn to Swim Pricing */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeIn} className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Learn to Swim Programs</h2>
            <p className="text-lg text-slate-600">Individual and group lessons for all ages</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Children's Lessons */}
            <motion.div {...fadeIn} transition={{ delay: 0.1 }}>
              <Card className="h-full border-2 hover:border-cyan-400 transition-all shadow-lg hover:shadow-xl">
                <CardHeader className="text-center pb-2">
                  <Badge className="w-fit mx-auto mb-2 bg-cyan-100 text-cyan-700">Ages 6 months - 16 years</Badge>
                  <CardTitle className="text-2xl text-slate-900">Children&apos;s Lessons</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-center">
                    <p className="text-slate-600 mb-4">Group lessons with certified instructors. Maximum 4-6 students per group.</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">Per Session</span>
                        <span className="text-2xl font-bold text-cyan-700">GHS 150</span>
                      </div>
                <p className="text-sm text-slate-500">45 minutes - 1 hour per session</p>
                    </div>
                    
                    <div className="p-4 bg-cyan-50 rounded-xl border-2 border-cyan-200">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <span className="font-medium">12 Sessions Package</span>
                          <Badge className="ml-2 bg-green-100 text-green-700">Best Value</Badge>
                        </div>
                        <span className="text-2xl font-bold text-cyan-700">GHS 1,500</span>
                      </div>
                        <p className="text-sm text-slate-500">Save GHS 300 (2 free sessions!)</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold text-slate-900">What&apos;s Included:</h4>
                    <ul className="space-y-2">
                      {['Certified instructor supervision', 'Water safety education', 'Progress tracking', 'Certificate on completion'].map((item, i) => (
                        <li key={i} className="flex items-center text-sm text-slate-600">
                          <Check className="w-4 h-4 text-green-500 mr-2" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link to="/book-lesson">
                    <Button className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="book-children-btn">
                      Book Children&apos;s Lesson
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>

            {/* Adult Lessons */}
            <motion.div {...fadeIn} transition={{ delay: 0.2 }}>
              <Card className="h-full border-2 hover:border-blue-400 transition-all shadow-lg hover:shadow-xl">
                <CardHeader className="text-center pb-2">
                  <Badge className="w-fit mx-auto mb-2 bg-blue-100 text-blue-700">Ages 17+</Badge>
                  <CardTitle className="text-2xl text-slate-900">Adult Lessons</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-center">
                    <p className="text-slate-600 mb-4">Learn to swim at any age. Private and group options available.</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">Per Session</span>
                        <span className="text-2xl font-bold text-blue-700">GHS 180</span>
                      </div>
                <p className="text-sm text-slate-500">45 minutes - 1 hour per session</p>
                    </div>
                    
                    <div className="p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <span className="font-medium">12 Sessions Package</span>
                          <Badge className="ml-2 bg-green-100 text-green-700">Best Value</Badge>
                        </div>
                        <span className="text-2xl font-bold text-blue-700">GHS 1,800</span>
                      </div>
                        <p className="text-sm text-slate-500">Save GHS 360 (2 free sessions!)</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold text-slate-900">What&apos;s Included:</h4>
                    <ul className="space-y-2">
                      {['Personalized instruction', 'Flexible scheduling', 'Technique coaching', 'Fitness improvement focus'].map((item, i) => (
                        <li key={i} className="flex items-center text-sm text-slate-600">
                          <Check className="w-4 h-4 text-green-500 mr-2" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link to="/book-lesson">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700" data-testid="book-adults-btn">
                      Book Adult Lesson
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Marlins Swim Team */}
      <section className="py-20 bg-linear-to-br from-orange-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeIn} className="text-center mb-12">
            <img 
              src="/marlins-logo.svg"
              alt="Marlins Swim Team"
              className="h-20 w-auto mx-auto mb-4"
            />
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Marlins Swim Team</h2>
            <p className="text-lg text-slate-600">Competitive swimming for dedicated athletes</p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <motion.div {...fadeIn}>
              <Card className="border-2 border-orange-300 shadow-xl">
                <CardHeader className="bg-linear-to-r from-orange-100 to-orange-50">
                  <CardTitle className="flex items-center text-orange-700 text-2xl">
                    <Trophy className="w-7 h-7 mr-3" />
                    Membership Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8">
                  {/* Registration Fee */}
                  <div className="p-6 bg-orange-50 rounded-2xl mb-8 border-2 border-orange-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-xl font-bold text-slate-900">Registration Fee</h3>
                        <p className="text-slate-600">One-time fee (includes T-shirt & swim cap)</p>
                      </div>
                      <span className="text-3xl font-bold text-orange-600">GHS 700</span>
                    </div>
                  </div>

                  {/* Subscription Options */}
                  <h3 className="text-xl font-bold text-slate-900 mb-6">Subscription Options</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    <div className="p-4 bg-slate-50 rounded-xl border hover:border-orange-300 transition-all">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">Annual</span>
                        <span className="font-bold text-orange-600">GHS 10,800/year</span>
                      </div>
                      <p className="text-sm text-slate-500">Full year commitment</p>
                    </div>
                    
                    <div className="p-4 bg-slate-50 rounded-xl border hover:border-orange-300 transition-all">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">Monthly</span>
                        <span className="font-bold text-orange-600">GHS 1,000/month</span>
                      </div>
                      <p className="text-sm text-slate-500">Flexible month-to-month</p>
                    </div>
                    
                    <div className="p-4 bg-green-50 rounded-xl border-2 border-green-200">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <span className="font-semibold">Quarterly</span>
                          <Badge className="ml-2 bg-green-100 text-green-700">Save GHS 600/yr</Badge>
                        </div>
                        <span className="font-bold text-orange-600">GHS 950/month</span>
                      </div>
                      <p className="text-sm text-slate-500">Billed every 3 months</p>
                    </div>
                    
                    <div className="p-4 bg-green-50 rounded-xl border-2 border-green-200">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <span className="font-semibold">Half-Yearly</span>
                          <Badge className="ml-2 bg-green-100 text-green-700">Save GHS 1,200/yr</Badge>
                        </div>
                        <span className="font-bold text-orange-600">GHS 900/month</span>
                      </div>
                      <p className="text-sm text-slate-500">Billed every 6 months</p>
                    </div>
                  </div>

                  {/* Family Discounts */}
                  <div className="p-6 bg-purple-50 rounded-2xl mb-8 border-2 border-purple-200">
                    <h4 className="font-bold text-purple-700 mb-3 flex items-center">
                      <Users className="w-5 h-5 mr-2" />
                      Family Discounts
                    </h4>
                    <div className="space-y-2 text-slate-700">
                      <p className="flex items-center">
                        <Star className="w-4 h-4 text-yellow-500 mr-2" />
                        <strong>2nd Child:</strong>&nbsp;10% off subscription
                      </p>
                      <p className="flex items-center">
                        <Star className="w-4 h-4 text-yellow-500 mr-2" />
                        <strong>3rd+ Child:</strong>&nbsp;15% off subscription
                      </p>
                    </div>
                  </div>

                  {/* Training Schedule */}
                  <div className="p-6 bg-slate-100 rounded-2xl mb-8">
                    <h4 className="font-bold text-slate-900 mb-4 flex items-center">
                      <Clock className="w-5 h-5 mr-2" />
                      Training Schedule
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-semibold text-slate-700">Monday - Friday</p>
                        <p className="text-slate-600">5:30 AM - 7:00 AM</p>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-700">Tuesday - Friday</p>
                        <p className="text-slate-600">4:30 PM - 6:00 PM</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="font-semibold text-slate-700">Saturday</p>
                        <p className="text-slate-600">10:00 AM - 12:00 PM or 3:00 PM - 5:00 PM</p>
                      </div>
                    </div>
                  </div>

                  <Link to="/swim-team">
                    <Button className="w-full bg-orange-500 hover:bg-orange-600 py-6 text-lg" data-testid="join-marlins-btn">
                      Join the Marlins Team
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Session Schedule Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeIn} className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Session Information</h2>
            <p className="text-lg text-slate-600">Everything you need to know about booking</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: Clock,
                title: 'Session Duration',
                description: 'Each lesson is 45 minutes to 1 hour. We recommend at least 2 sessions per week for optimal progress.',
                color: 'cyan'
              },
              {
                icon: Calendar,
                title: 'Flexible Booking',
                description: 'Choose from weekday and weekend slots. Morning, afternoon, and evening sessions available.',
                color: 'blue'
              },
              {
                icon: DollarSign,
                title: 'Payment Options',
                description: 'Pay per session or save with packages. Mobile Money (MTN, Vodafone) and card payments accepted.',
                color: 'green'
              }
            ].map((item, i) => (
              <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.1 }}>
                <Card className="h-full text-center hover:shadow-lg transition-shadow">
                  <CardContent className="pt-8">
                    <div className={`w-14 h-14 mx-auto mb-4 rounded-full bg-${item.color}-100 flex items-center justify-center`}>
                      <item.icon className={`w-7 h-7 text-${item.color}-600`} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                    <p className="text-slate-600">{item.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-linear-to-r from-cyan-600 to-cyan-700 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeIn}>
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Swimming?</h2>
            <p className="text-xl text-cyan-100 mb-8">
              Book your first lesson today and experience the joy of swimming with Streamline Aquatics.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book-lesson">
                <Button size="lg" className="bg-white text-cyan-700 hover:bg-slate-100 px-8 py-6 text-lg">
                  Book a Lesson
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-cyan-800 px-8 py-6 text-lg">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SchedulesPricing;
